/**
 * ExampleQuestions Component  (Round 4: "Student Examples")
 *
 * NOTE: despite the legacy name, this screen no longer shows example/priming
 * questions. Under the staged-quiz design participants answer real questions
 * mid-learning, so the example preview was removed. This component now renders
 * ONLY the Student's pre-chat learning-confidence self-assessment, which saves
 * `baseline_learning_confidence` (a measure that must be preserved).
 *
 * The Teacher in Round 4 is routed to RoleInstructions (see Stage.jsx), so this
 * component is only mounted for the Student. A defensive fallback is kept in
 * case it is ever rendered for a non-student.
 *
 * (Optional future cleanup: rename file/component to PreChatSelfAssessment.)
 */

import React, { useState } from "react";
import { usePlayer, useGame } from "@empirica/core/player/classic/react";

// Student: Learning Confidence questions (2 questions)
const STUDENT_QUESTIONS = [
  {
    id: "learn_q1",
    question: "Ask good questions when something is unclear"
  },
  {
    id: "learn_q2",
    question: "Recognize when you don't fully understand something"
  }
];

const LIKERT_OPTIONS = [
  { value: "0", label: "0" },
  { value: "1", label: "1" },
  { value: "2", label: "2" },
  { value: "3", label: "3" },
  { value: "4", label: "4" },
  { value: "5", label: "5" },
  { value: "6", label: "6" }
];

export function ExampleQuestions({ next }) {
  const player = usePlayer();
  const game = useGame();

  const config = game.get("config");
  const topicName = config?.name || "the topic";

  const marker = player.get("marker");
  const isStudent = marker > 0;

  // State for student self-assessment answers
  const [studentAnswers, setStudentAnswers] = useState({});

  const handleAnswerChange = (questionId, value) => {
    setStudentAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const allStudentQuestionsAnswered = STUDENT_QUESTIONS.every(q => studentAnswers[q.id]);

  const handleContinue = () => {
    // Save self-assessment answers before continuing
    if (isStudent && allStudentQuestionsAnswered) {
      player.set("baseline_learning_confidence", studentAnswers);
      player.set("baseline_assessment_timestamp", new Date().toISOString());
    }
    next();
  };

  // Defensive fallback: this screen is only mounted for the Student. If a
  // non-student ever lands here, just let them continue.
  if (!isStudent) {
    return (
      <div className="h-full w-full flex items-center justify-center bg-gray-50">
        <button
          onClick={() => next()}
          className="py-3 px-6 rounded-lg font-semibold text-white bg-blue-600 hover:bg-blue-700"
        >
          Continue
        </button>
      </div>
    );
  }

  const canContinue = allStudentQuestionsAnswered;

  return (
    <div className="h-full overflow-y-auto bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="bg-emerald-600 rounded-xl p-4 mb-6 text-white text-center">
          <span className="text-sm font-medium uppercase tracking-wide opacity-80">Student</span>
          <h2 className="text-xl font-bold mt-1">Pre-Chat Self-Assessment</h2>
          <p className="text-sm opacity-80 mt-1">Before learning from your partner</p>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <p className="text-gray-600 mb-6">
            Your partner is ready to teach you about <strong className="text-emerald-700">{topicName}</strong>.
            Before the chat begins, please rate your confidence:
          </p>

          {/* Self-Assessment Questions */}
          <div className="mb-6 space-y-4">
            <h4 className="text-base font-semibold text-gray-800">
              How confident are you in your ability to... (0 = Not at all, 6 = Very confident)
            </h4>

            {STUDENT_QUESTIONS.map((q, index) => (
              <div key={q.id} className="p-4 bg-gray-50 rounded-lg">
                <p className="text-gray-700 mb-3">
                  {index + 1}. {q.question}
                </p>
                <div className="flex justify-between items-center gap-2">
                  {LIKERT_OPTIONS.map(option => (
                    <label
                      key={option.value}
                      className={`flex-1 text-center py-2 px-1 rounded-lg cursor-pointer transition-all ${
                        studentAnswers[q.id] === option.value
                          ? 'bg-emerald-600 text-white'
                          : 'bg-white border border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <input
                        type="radio"
                        name={q.id}
                        value={option.value}
                        checked={studentAnswers[q.id] === option.value}
                        onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                        className="sr-only"
                      />
                      {option.label}
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Quiz reminder */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6">
            <p className="text-sm text-amber-800">
              <strong>Remember:</strong> You will be tested on this topic.
            </p>
          </div>

          {/* Submit Button */}
          <button
            onClick={handleContinue}
            disabled={!canContinue}
            className={`w-full py-3 px-6 rounded-lg font-semibold text-white transition-all duration-200 ${
              canContinue
                ? 'bg-emerald-600 hover:bg-emerald-700'
                : 'bg-gray-400 cursor-not-allowed'
            }`}
          >
            {canContinue ? "I'm Ready to Learn" : "Please answer all questions"}
          </button>
        </div>
      </div>
    </div>
  );
}
